import { APIs, LOCAL_STORAGE_KEYS } from "../constants/Constants";
import api from "../apis/api";
import storage from "./storage";

// Map API response contact fields to UI field names
const mapContactFromApi = (apiContact) => ({
  contactId: apiContact.contactId,
  name: apiContact.fullName,
  phone: apiContact.phoneNumber,
  dialCode: apiContact.phoneCountryCode,
  countryCode: apiContact.phoneCountryIso,
  email: apiContact.email,
  company: apiContact.company,
  notes: apiContact.notes,
  isFavorite: apiContact.isFavorite,
  createdAt: apiContact.createdAt,
  updatedAt: apiContact.updatedAt,
});

// Map UI sort field names to API SortBy parameter values
const mapSortFieldToApi = (uiSortField) => {
  const mapping = {
    name: "fullname",
    company: "companyname",
    email: "email",
    createdAt: "createdat",
  };
  return mapping[uiSortField] || "fullname";
};

// Map UI searchBy field names to API SearchBy parameter values
const mapSearchByFieldToApi = (uiSearchByField) => {
  const mapping = {
    name: "fullname",
    phone: "mobileno",
    email: "email",
    company: "companyname",
  };
  return mapping[uiSearchByField] || null;
};

// Get contacts from localStorage (used by mock methods that don't have APIs yet)
const getStoredContacts = () => {
  const contacts = storage.get(LOCAL_STORAGE_KEYS.ADDRESS_BOOK_CONTACTS);
  if (!contacts || !Array.isArray(contacts)) {
    return [];
  }
  return contacts;
};

// Save contacts to localStorage (used by mock methods)
const saveContacts = (contacts) => {
  storage.set(LOCAL_STORAGE_KEYS.ADDRESS_BOOK_CONTACTS, contacts);
};

// Simulate async delay for mock methods
const delay = (ms = 300) => new Promise(resolve => setTimeout(resolve, ms));

const addressBookService = {
  /**
   * Get contacts with server-side pagination, search, and filter
   * Uses real API: GET /api/contacts
   */
  async getContacts(params = {}) {
    const {
      search = '',
      searchBy = 'all',
      favoritesOnly = false,
      companies = [],
      fromDate = null,
      toDate = null,
      sortField = 'name',
      sortOrder = 'asc',
      skip = 0,
      limit = 50,
    } = params;

    const queryParams = new URLSearchParams();
    if (search) queryParams.append('Search', search);
    const apiSearchBy = mapSearchByFieldToApi(searchBy);
    if (search && apiSearchBy) queryParams.append('SearchBy', apiSearchBy);
    queryParams.append('FavoritesOnly', favoritesOnly);
    if (companies.length > 0) {
      companies.forEach(c => queryParams.append('Companies', c));
    }
    if (fromDate) queryParams.append('FromDate', fromDate);
    if (toDate) queryParams.append('ToDate', toDate);
    queryParams.append('SortBy', mapSortFieldToApi(sortField));
    queryParams.append('SortOrder', sortOrder);
    queryParams.append('Skip', skip);
    queryParams.append('Limit', limit);

    const url = `${APIs.CONTACTS.GET_ALL}?${queryParams.toString()}`;
    const response = await api.get(url);

    return {
      items: (response.allContacts || []).map(mapContactFromApi),
      totalCount: response.totalCount || 0,
      favoritesCount: response.favoritesCount || 0,
      allContactsCount: response.allContactsCount || 0,
      skip: response.skip || 0,
      limit: response.limit || limit,
    };
  },

  /**
   * Get contacts for the ContactPicker (invite modal).
   * Uses GET /api/contacts/all — simpler params: Search, Skip, Limit only.
   * Favorites are returned first by the API.
   */
  async getContactsForPicker({ search = '', skip = 0, limit = 50 } = {}) {
    const queryParams = new URLSearchParams();
    if (search) queryParams.append('Search', search);
    queryParams.append('Skip', skip);
    queryParams.append('Limit', limit);

    const url = `${APIs.CONTACTS.GET_ALL_SIMPLE}?${queryParams.toString()}`;
    const response = await api.get(url);

    const items = (response.allContacts || []).map(mapContactFromApi);
    return {
      items,
      totalCount: response.totalCount || 0,
      skip: response.skip ?? skip,
      limit: response.limit ?? limit,
    };
  },

  /**
   * Get a single contact by ID
   * Uses real API: GET /api/contacts/{ContactId}
   */
  async getContactById(contactId) {
    const url = `${APIs.CONTACTS.GET_ONE}/${contactId}`;
    const response = await api.get(url);
    return mapContactFromApi(response);
  },

  /**
   * Create a new contact
   */
  async createContact(contactData) {
    const phone = contactData.phone?.trim() || '';
    const email = contactData.email?.trim() || null;

    const requestPayload = {
      fullName: contactData.name?.trim() || '',
      company: contactData.company?.trim() || '',
      notes: contactData.notes?.trim() || '',
      isFavorite: contactData.isFavorite || false,
      phoneNumber: phone,
      phoneCountryCode: phone ? (contactData.dialCode || '') : '',
      phoneCountryIso: phone ? (contactData.countryCode || '') : '',
      email: email,
    };

    const response = await api.post(APIs.CONTACTS.CREATE, requestPayload);
    return mapContactFromApi(response);
  },

  /**
   * Update an existing contact
   * Uses real API: PUT /api/contacts/{ContactId}
   */
  async updateContact(contactId, contactData) {
    const phone = contactData.phone?.trim() || '';
    const email = contactData.email?.trim() || null;

    const requestPayload = {
      fullName: contactData.name?.trim() || '',
      company: contactData.company?.trim() || '',
      notes: contactData.notes?.trim() || '',
      isFavorite: contactData.isFavorite || false,
      phoneNumber: phone,
      phoneCountryCode: phone ? (contactData.dialCode || '') : '',
      phoneCountryIso: phone ? (contactData.countryCode || '') : '',
      email: email,
    };

    const url = `${APIs.CONTACTS.UPDATE}/${contactId}`;
    const response = await api.put(url, requestPayload);
    return mapContactFromApi(response);
  },

  /**
   * Delete a contact
   * Uses real API: DELETE /api/contacts
   */
  async deleteContact(contactId) {
    const response = await api.delete(APIs.CONTACTS.DELETE, { data: { contactIds: [contactId] } });
    return response;
  },

  /**
   * Bulk delete contacts
   * Uses real API: DELETE /api/contacts
   */
  async bulkDeleteContacts(contactIds) {
    const response = await api.delete(APIs.CONTACTS.DELETE, { data: { contactIds } });
    return response;
  },

  /**
   * Toggle favorite for single/multiple contacts
   * Uses real API: PATCH /api/contacts/favorite
   */
  async toggleFavorite(contactIds, isFavorite) {
    const response = await api.patch(APIs.CONTACTS.TOGGLE_FAVORITE, {
      contactIds: Array.isArray(contactIds) ? contactIds : [contactIds],
      isFavorite,
    });
    return response;
  },

  /**
   * Import contacts from file (.xlsx or .csv)
   * Uses real API: POST /api/contacts/import
   */
  async importContacts(file) {
    const formData = new FormData();
    formData.append('File', file);
    const response = await api.postForm(APIs.CONTACTS.IMPORT, formData);
    return response;
  },

  /**
   * Export contacts to Excel (.xlsx)
   * Uses real API: POST /api/contacts/export
   */
  async exportContacts(params = {}) {
    const {
      contactIds = [],
      columns = [],
      search = '',
      searchBy = 'all',
      favoritesOnly = false,
      companies = [],
      fromDate = null,
      toDate = null,
      sortField = 'name',
      sortOrder = 'asc',
    } = params;

    const requestPayload = {
      sortBy: mapSortFieldToApi(sortField),
      sortOrder,
      favoritesOnly,
    };

    if (contactIds.length > 0) requestPayload.contactIds = contactIds;
    if (columns.length > 0) requestPayload.columns = columns;
    if (search) requestPayload.search = search;
    const apiSearchBy = mapSearchByFieldToApi(searchBy);
    if (search && apiSearchBy) requestPayload.searchBy = apiSearchBy;
    if (companies.length > 0) requestPayload.companies = companies;
    if (fromDate) requestPayload.fromDate = fromDate;
    if (toDate) requestPayload.toDate = toDate;

    const response = await api.postBlob(APIs.CONTACTS.EXPORT, requestPayload);
    return response;
  },

  /**
   * Search contacts - uses real API
   */
  async searchContacts(query, limit = 10) {
    const url = `${APIs.CONTACTS.GET_ALL}?Search=${encodeURIComponent(query || '')}&Limit=${limit}&SortBy=fullname&SortOrder=asc`;
    const response = await api.get(url);
    return (response.allContacts || []).map(mapContactFromApi);
  },

  /**
   * Increment session count for a contact (mock - no API yet)
   */
  async incrementSessionCount(contactId) {
    await delay(100);

    const contacts = getStoredContacts();
    const index = contacts.findIndex(c => c.contactId === contactId);

    if (index === -1) {
      throw new Error('Contact not found');
    }

    contacts[index].sessionCount = (contacts[index].sessionCount || 0) + 1;
    contacts[index].lastActivityAt = new Date().toISOString();
    saveContacts(contacts);

    return contacts[index];
  },
};

export default addressBookService;
